import {type ComponentType, lazy, type LazyExoticComponent} from 'react';

import {importWithRetry} from './dynamicImportRetry';

type AsyncLoader<T> = () => Promise<T>;

interface PreloadableLazyComponent<TModule, T extends ComponentType<any>> {
  Component: LazyExoticComponent<T>;
  /** Starts the module request early, so Suspense has less or nothing to wait for. */
  preload: AsyncLoader<TModule>;
}

/**
 * Memoizes one module load and allows a later call to retry after failure.
 * A transient chunk failure is retried under a cache-busted URL first, and
 * reloads the page as a last resort — see {@link importWithRetry}.
 */
export function createRetryableModuleLoader<T>(
  loadModule: AsyncLoader<T>
): AsyncLoader<T> {
  let modulePromise: Promise<T> | undefined;

  return () => {
    modulePromise ??= importWithRetry(loadModule).catch(error => {
      modulePromise = undefined;
      throw error;
    });
    return modulePromise;
  };
}

/**
 * The way to lazy-load a component: `React.lazy` alone turns a chunk fetch that
 * failed in transit into a full-page crash.
 *
 * Returns the component and a preload function sharing one module load, so a
 * hover or focus can start the request before the component renders.
 *
 * Pass the dynamic import at the call site so the bundler can discover its
 * literal path and keep the module in a separate chunk. `loadModule` must
 * perform exactly one import and return its module, a constraint recovery
 * imposes ({@link importWithRetry}); `selectComponent` covers a module that
 * exports the component under a name, and defaults to the default export.
 */
export function createPreloadableLazyComponent<T extends ComponentType<any>>(
  loadModule: AsyncLoader<{default: T}>
): PreloadableLazyComponent<{default: T}, T>;
export function createPreloadableLazyComponent<
  TModule,
  T extends ComponentType<any>,
>(
  loadModule: AsyncLoader<TModule>,
  selectComponent: (module: TModule) => T
): PreloadableLazyComponent<TModule, T>;
export function createPreloadableLazyComponent<
  TModule,
  T extends ComponentType<any>,
>(
  loadModule: AsyncLoader<TModule>,
  selectComponent?: (module: TModule) => T
): PreloadableLazyComponent<TModule, T> {
  const preload = createRetryableModuleLoader(loadModule);
  const Component = lazy(async () => {
    const module = await preload();
    return selectComponent == null
      ? (module as {default: T})
      : {default: selectComponent(module)};
  });

  return {Component, preload};
}
